import { Alert, Box, Button, Grid, Group, Skeleton, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconFileOff, IconHeadset, IconInfoCircle } from "@tabler/icons-react";
import { useState } from "react";
import { MyChargeContractApi } from "../../../apis";
import { EmptyState } from "../../../components/billing/EmptyState";
import { SectionHeader } from "../../../components/billing/SectionHeader";
import { Message } from "../../../utils/message";
import { BillingTerms } from "./components/BillingTerms";
import { ComponentCard } from "./components/ComponentCard";
import { ContractHero } from "./components/ContractHero";
import { useChargeContract } from "./hooks/useChargeContract";

export function ContractTab() {
  const { contract, error, isLoading, reload } = useChargeContract();
  const [pdfLoading, setPdfLoading] = useState(false);

  const handleViewPdf = async () => {
    if (!contract) return;
    try {
      setPdfLoading(true);
      await MyChargeContractApi.openContractPdf(contract.id);
    } catch {
      Message.error("PDF нээхэд алдаа гарлаа");
    } finally {
      setPdfLoading(false);
    }
  };

  if (error) {
    return (
      <Alert
        color="red"
        icon={<IconAlertTriangle size={18} />}
        title="Гэрээ ачааллахад алдаа гарлаа"
      >
        <Button mt="sm" size="xs" variant="light" color="red" onClick={reload}>
          Дахин ачаалах
        </Button>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={140} radius="md" />
        <Grid gap="lg">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              <Skeleton height={88} radius="md" />
              <Skeleton height={88} radius="md" />
              <Skeleton height={88} radius="md" />
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Skeleton height={280} radius="md" />
          </Grid.Col>
        </Grid>
      </Stack>
    );
  }

  if (!contract) {
    return <EmptyState icon={<IconFileOff size={26} />} message="Идэвхтэй гэрээ алга байна." />;
  }

  const components = contract.components ?? [];
  // Best-effort next-invoice estimate: sum of enabled recurring component amounts.
  const nextEstimate = components
    .filter((c) => c.componentType === "RECURRING_SUBSCRIPTION" && c.isEnabled !== false)
    .reduce((sum, c) => sum + Number(c.recurringAmount ?? 0), 0);

  return (
    <Stack gap="lg">
      <ContractHero contract={contract} onViewPdf={handleViewPdf} pdfLoading={pdfLoading} />

      <Grid gap="lg">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <SectionHeader eyebrow="Гэрээний нөхцөл" title="Төлбөрийн бүрэлдэхүүн" />
            {components.length === 0 ? (
              <EmptyState
                icon={<IconFileOff size={26} />}
                message="Энэ гэрээнд төлбөрийн бүрэлдэхүүн бүртгэгдээгүй байна."
              />
            ) : (
              components.map((c) => (
                <ComponentCard key={c.id} component={c} contractCurrency={contract.currency} />
              ))
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <BillingTerms contract={contract} nextEstimate={nextEstimate} />
        </Grid.Col>
      </Grid>

      <Alert color="brand" variant="light" icon={<IconInfoCircle size={18} />}>
        <Group justify="space-between" align="center" wrap="wrap" gap="sm">
          <Box>
            <Text fw={600}>Гэрээнд өөрчлөлт хийх үү?</Text>
            <Text size="sm" c="dimmed">
              Гэрээний нөхцөлийг шинэчлэхийн тулд DeHUB-ийн account менежертэйгээ холбогдоно уу.
            </Text>
          </Box>
          <Button variant="light" color="brand" leftSection={<IconHeadset size={18} />}>
            Менежертэй холбогдох
          </Button>
        </Group>
      </Alert>
    </Stack>
  );
}
